import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronLeft, ChevronRight, RotateCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FlashcardViewer() {
  const { id } = useParams();
  const [flashcard, setFlashcard] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFlashcard();
  }, [id]);

  const loadFlashcard = async () => {
    try {
      const response = await axios.get(`${API}/flashcards/${id}`);
      setFlashcard(response.data);
    } catch (error) {
      console.error('Failed to load flashcard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < flashcard.cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-20 text-center">
          <div className="text-2xl text-[#6B4423]">Loading flashcards...</div>
        </div>
      </Layout>
    );
  }

  if (!flashcard) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-20 text-center">
          <h1 className="text-3xl retro-title text-[#2C1810] mb-4">Flashcard Not Found</h1>
          <Link to="/flashcards-center" className="retro-button">
            Back to Flashcards
          </Link>
        </div>
      </Layout>
    );
  }

  const currentCard = flashcard.cards[currentIndex];

  return (
    <Layout>
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link to="/flashcards-center" className="inline-flex items-center gap-2 bg-[#2C1810]/90 text-[#F4E4C1] font-bold px-4 py-2 rounded-lg hover:bg-[#2C1810] transition-all mb-4">
            <ArrowLeft className="w-5 h-5" />
            Back to Flashcards
          </Link>
          <h1 className="text-4xl retro-title text-[#2C1810] mb-2">{flashcard.test_title}</h1>
          <div className="flex items-center gap-3">
            <span className="px-3 py-1 bg-[#8B5A3C] text-white text-sm font-bold rounded">
              {flashcard.tier}
            </span>
            <span className="px-3 py-1 bg-[#E8934D] text-white text-sm font-bold rounded">
              {flashcard.category}
            </span>
          </div>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-[#6B4423]">
              Card {currentIndex + 1} of {flashcard.cards.length}
            </span>
            <span className="text-sm text-[#6B4423]">
              {Math.round(((currentIndex + 1) / flashcard.cards.length) * 100)}% Complete
            </span>
          </div>
          <div className="w-full h-2 bg-[#E8B67A] rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#8B5A3C] to-[#6B4423] transition-all duration-300"
              style={{ width: `${((currentIndex + 1) / flashcard.cards.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Flashcard */}
        <div className="max-w-3xl mx-auto mb-8">
          <div 
            className="relative cursor-pointer"
            onClick={handleFlip}
            style={{ minHeight: '400px' }}
          >
            {/* Front of card (Question) */}
            {!isFlipped && (
              <div 
                className="cassette-card p-12 absolute inset-0"
                style={{ minHeight: '400px' }}
              >
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="text-sm font-bold text-[#6B4423] mb-6 uppercase tracking-wide">
                    Question
                  </div>
                  <div className="text-3xl font-bold text-[#2C1810] leading-relaxed" data-testid="flashcard-question">
                    {currentCard.question}
                  </div>
                  <div className="mt-8 flex items-center gap-2 text-[#6B4423] text-sm">
                    <RotateCw className="w-4 h-4" />
                    <span>Click to flip</span>
                  </div>
                </div>
              </div>
            )}

            {/* Back of card (Answer) */}
            {isFlipped && (
              <div 
                className="cassette-card p-12 bg-gradient-to-br from-[#E8934D] to-[#D4722A] text-white absolute inset-0"
                style={{ minHeight: '400px' }}
              >
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="text-sm font-bold mb-6 uppercase tracking-wide opacity-90">
                    Answer
                  </div>
                  <div className="text-2xl font-semibold leading-relaxed" data-testid="flashcard-answer">
                    {currentCard.answer}
                  </div>
                  <div className="mt-8 flex items-center gap-2 text-sm opacity-90">
                    <RotateCw className="w-4 h-4" />
                    <span>Click to flip back</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation */}
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-3">
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className="retro-button flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            <ChevronLeft className="w-5 h-5" />
            Previous
          </button>

          <button
            onClick={handleFlip}
            className="retro-button bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <RotateCw className="w-5 h-5" />
            Flip Card
          </button>

          <button
            onClick={handleNext}
            disabled={currentIndex === flashcard.cards.length - 1}
            className="retro-button flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
          >
            Next
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </Layout>
  );
}
